import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { BehaviorSubject, EMPTY, merge, throttleTime } from 'rxjs';
import { filter, shareReplay, take } from 'rxjs/operators';

import { AnalyticsSegmentAdapter } from '@core/analytics-legacy';
import { filterBoolean } from '@core/common-legacy';
import { WindowRef } from '@core/user-interface-legacy';

import { AnalyticsIntercomAdapter } from './intercom-analytics.adapter';
import { IntercomAction, Recipients } from './types';

declare const Intercom: (action: IntercomAction, data?: any) => void;

@Injectable({
  providedIn: 'root',
})
export class IntercomService {
  public static readonly settings: { hide_default_launcher: boolean; vertical_padding: number } = {
    hide_default_launcher: true,
    vertical_padding: 70,
  };

  private readonly windowRef = inject(WindowRef);
  private readonly router = inject(Router);
  private readonly intercomAdapter = inject(AnalyticsIntercomAdapter, { optional: true });
  private readonly segmentAdapter = inject(AnalyticsSegmentAdapter, { optional: true });

  public chatOpen$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public unreadCount$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);

  private intercomReady$ = (
    this.intercomAdapter?.ready$?.asObservable() ??
    this.segmentAdapter?.ready$?.asObservable() ??
    EMPTY
  ).pipe(
    filter(() => this.intercomAvailable),
    take(1),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor() {
    this.intercomReady$.subscribe(() => {
      this.attachIntercomAPIListeners();
      this.updateOnChatAndNavChange();
    });
  }

  public open(recipients?: Recipients): void {
    this.intercomAvailable ? Intercom(IntercomAction.Show) : this.openMailClient(recipients);
  }

  public hide(): void {
    if (!this.intercomAvailable) return;

    Intercom(IntercomAction.Hide);
  }

  public update(data?: {}): void {
    if (!this.intercomAvailable) return;

    Intercom(IntercomAction.Update, { ...IntercomService.settings, ...data });
  }

  public startNewMessage(message: string = '', recipients?: Recipients): void {
    this.intercomAvailable
      ? Intercom(IntercomAction.ShowNewMessage, message)
      : this.openMailClient(recipients, message);
  }

  public shutdown(): void {
    if (!this.intercomAvailable) return;

    Intercom(IntercomAction.Shutdown);
  }

  public scheduleSurvey(surveyId: number | string): void {
    if (this.intercomAdapter || this.segmentAdapter) {
      this.intercomReady$.subscribe(() => {
        Intercom(IntercomAction.startSurvey, surveyId);
      });
    } else {
      console.warn(
        `Attempted to display survey ${surveyId}, but no analytics adapter is provided.`,
      );
    }
  }

  private get intercomAvailable(): boolean {
    /* Intercom is available on production
     * initialized by the segment cdn https://cdn.segment.com/analytics.js
     * Intercom is also available on staging and dynamic environments
     * initialized manually to our testing intercom env
     */
    return typeof Intercom !== 'undefined';
  }

  public openMailClient(recipients?: Recipients, subject: string = ''): void {
    this.windowRef.open(
      `mailto:${recipients ? recipients : Recipients.Support}?subject=${subject}`,
    );
  }

  private attachIntercomAPIListeners(): void {
    Intercom(IntercomAction.onHide, () => {
      this.chatOpen$.next(false);
    });
    Intercom(IntercomAction.onShow, () => {
      this.chatOpen$.next(true);
    });
    Intercom(IntercomAction.onUnreadCountChange, (count: number) => {
      this.unreadCount$.next(count);
    });
  }

  private updateOnChatAndNavChange(): void {
    merge(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        throttleTime(200),
      ),
      this.chatOpen$.pipe(filterBoolean),
    ).subscribe(() => this.update());
  }
}
